import { useEffect, useState } from 'react';

/**
 * `<img>` para recursos que EXIGEN cabecera `Authorization`.
 *
 * Existe por una limitación concreta: una etiqueta `<img src>` no manda
 * cabeceras, así que un endpoint autenticado no se puede pintar directamente.
 * La salida fácil sería dejar el endpoint público (que es lo que hacía Kivai
 * heredado de Toki, donde las fotos eran de la vía pública) o meter el token en
 * la URL — pero una URL acaba en los logs del proxy, en el `Referer` y en el
 * historial del navegador, así que sería cambiar un agujero por otro.
 *
 * Lo que se hace es descargar con `fetch`, envolver la respuesta en un blob y
 * pintar ese object URL. El token viaja en la cabecera y nunca en la URL.
 *
 * El object URL se revoca al desmontar y al cambiar de foto: si no, cada
 * imagen que se mira se queda en memoria hasta recargar la página, y un hilo
 * con diez capturas las acumularía todas.
 */
interface AuthedImageProps {
  src: string;
  token: string;
  /** El tipo de la imagen (paso 34). Los adjuntos se sirven como
   *  `application/octet-stream` para que nada los interprete; el blob se
   *  vuelve a envolver con su tipo real, que el backend comprobó por la firma. */
  type?: string;
  alt: string;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
}

export function AuthedImage({ src, token, type, alt, className, onClick }: AuthedImageProps) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let revoked = false;
    let current: string | null = null;
    setFailed(false);

    fetch(src, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        return r.blob();
      })
      .then((blob) => {
        // Si el efecto ya se limpió (cambio rápido de foto), no montamos un
        // object URL que nadie va a revocar.
        if (revoked) return;
        current = URL.createObjectURL(type ? new Blob([blob], { type }) : blob);
        setObjectUrl(current);
      })
      .catch(() => {
        if (!revoked) setFailed(true);
      });

    return () => {
      revoked = true;
      if (current) URL.revokeObjectURL(current);
      setObjectUrl(null);
    };
  }, [src, token, type]);

  // Mientras carga (o si falla) se devuelve un hueco del mismo tamaño en vez de
  // un `<img>` roto: el icono de imagen rota del navegador parece un dato
  // corrupto, y casi siempre es solo que aún no ha llegado.
  if (failed || !objectUrl) {
    return <span className={className} aria-hidden="true" onClick={onClick} />;
  }
  return <img src={objectUrl} alt={alt} className={className} onClick={onClick} />;
}
